import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UploadCloud, FileDown, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const COLS = [["matricula", "Matrícula"], ["student_name", "Aluno"], ["serie", "Série"], ["ano", "Ano"], ["school", "Estabelecimento"], ["subject", "Disciplina"], ["grade", "Nota"], ["ch", "C/H"], ["attendance", "Freq. %"], ["result", "Resultado"]];

const Stat = ({ label, value, cls = "text-slate-900", id }) => (
  <Card className="border-slate-200 p-4" data-testid={id}><div className={`font-display text-3xl font-black ${cls}`}>{value}</div><div className="text-xs font-medium text-slate-500">{label}</div></Card>
);

export const PriorGradesImport = ({ scopeParams }) => {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const download = async (format) => {
    try {
      const { data } = await api.get("/imports/prior-grades/template", { params: { format }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a"); a.href = url; a.download = `modelo_notas_anteriores.${format}`; a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      if (scopeParams.school_id) fd.append("school_id", scopeParams.school_id);
      const { data } = await api.post("/imports/prior-grades/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data); setResult(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const confirm = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/imports/prior-grades/${preview.id}/confirm`); setResult(data); setPreview(null); toast.success("Notas anteriores importadas"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="min-w-0 max-w-full space-y-4">
      <Card className="border-slate-200 p-5">
        <h3 className="mb-2 font-display font-semibold text-slate-800">Notas de séries anteriores (outra escola)</h3>
        <p className="mb-4 text-sm text-slate-500">Uma linha por aluno, série e disciplina. Colunas: Matrícula, Série, Ano, Estabelecimento, Município/UF, Resultado, Frequência %, Disciplina, Nota (0 a 10), C/H. Se o aluno já tiver notas daquela série, elas serão substituídas pelas da planilha.</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => download("xlsx")} data-testid="prior-template-xlsx"><FileDown className="mr-1.5 h-4 w-4" /> Modelo XLSX</Button>
          <Button variant="outline" onClick={() => download("csv")} data-testid="prior-template-csv"><FileDown className="mr-1.5 h-4 w-4" /> Modelo CSV</Button>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800" data-testid="prior-upload-label">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Enviar planilha
            <input type="file" accept=".xlsx,.csv" className="hidden" onChange={onFile} disabled={busy} data-testid="prior-file-input" />
          </label>
        </div>
      </Card>

      {preview && (
        <div className="min-w-0 max-w-full">
          <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Linhas" value={preview.summary.total} id="prior-stat-total" />
            <Stat label="Prontas" value={preview.summary.ok} cls="text-green-700" id="prior-stat-ok" />
            <Stat label="Erros" value={preview.summary.erros} cls="text-red-600" id="prior-stat-errors" />
            <Stat label="Alunos" value={preview.summary.alunos} id="prior-stat-students" />
          </div>
          <Card className="w-full max-w-[calc(100vw-2rem)] overflow-x-auto border-slate-200 sm:max-w-full" data-testid="prior-preview-table">
            <Table>
              <TableHeader><TableRow>{COLS.map(([, l]) => <TableHead key={l}>{l}</TableHead>)}<TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {preview.rows.map((r, i) => (
                  <TableRow key={i} data-testid={`prior-row-${i}`} className={r.status === "ERRO" ? "bg-red-50/40" : ""}>
                    {COLS.map(([k]) => <TableCell key={k} className="text-sm">{r[k] || "—"}</TableCell>)}
                    <TableCell>
                      <Badge className={r.status === "OK" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>{r.status}</Badge>
                      {r.issues.length > 0 && <div className="mt-1 text-xs text-red-600">{r.issues.join(", ")}</div>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          {preview.summary.erros > 0 && <p className="mt-2 text-xs text-amber-600">Linhas com erro NÃO serão importadas.</p>}
          <Button onClick={confirm} disabled={busy || preview.summary.ok === 0} data-testid="prior-confirm-button" className="mt-4 bg-blue-700 hover:bg-blue-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirmar importação (${preview.summary.ok})`}</Button>
        </div>
      )}

      {result && (
        <Card className="border-slate-200 p-6" data-testid="prior-result">
          <div className="flex items-center gap-3"><CheckCircle2 className="h-9 w-9 text-green-600" />
            <div><h3 className="font-display text-lg font-bold text-slate-900">Importação concluída</h3>
              <p className="text-sm text-slate-500">{result.alunos} aluno(s) · {result.series} série(s) atualizada(s) · {result.disciplinas} disciplina(s)</p></div>
          </div>
        </Card>
      )}
    </div>
  );
};
